/**
 * @file The Metrics chart's Plotly figure: one line trace per series line,
 * keyed and ordered by rank, over every served bucket (an empty bucket is a
 * zero, never a bridged gap), with the range slider and the mode bar.
 * Consumer: the lazy Metrics chart, the only importer of Plotly. Every hover
 * label and tick is text written here from the exact decimal strings, by
 * compact currency or grouped whole counts, so Plotly formats no number; the
 * plotted `y` and tick positions are geometry only. Tick steps are chosen in
 * whole-number BigInt arithmetic.
 */
import { DateTime } from 'luxon';
import type { GlobalToken } from 'antd';

import type { AnalyticsFraction, AnalyticsSeriesGrain } from '#types';
import { analyticsDecimalFraction, analyticsFractionDecimal } from '#types';

import { analyticsCompactCurrencyText } from './analyticsCompactCurrencyText';
import type {
  AnalyticsSeriesAvailableMetric,
  AnalyticsSeriesLine,
  MetricsSeriesFigure,
} from './analyticsMetricsTab.types';

const RANK_COLORS = [
  'blue',
  'green',
  'purple',
  'cyan',
  'gold',
  'magenta',
  'geekblue',
  'lime',
  'pink',
] as const satisfies readonly (keyof GlobalToken)[];

type MetricsSeriesToken = Pick<
  GlobalToken,
  | (typeof RANK_COLORS)[number]
  | 'colorPrimary'
  | 'colorTextTertiary'
  | 'colorTextSecondary'
  | 'colorBorderSecondary'
  | 'colorBgContainer'
  | 'fontFamily'
  | 'fontSizeSM'
>;

type MetricsSeriesMetric = Pick<
  AnalyticsSeriesAvailableMetric,
  'label' | 'unit'
>;

const TOP_RANK = 1;

const ALL_OTHERS_RANK = 11;

const DECADE = 10n;

const TICK_INTERVALS = 4n;

const NICE_MANTISSAS = [1n, 2n, 5n];

const MAX_DATE_TICKS = 8;

const LINE_WIDTH = 1.5;

const MARKER_SIZE = 5;

const GROUPED = new Intl.NumberFormat('en-US');

const HOVER_DATE: Record<AnalyticsSeriesGrain, string> = {
  day: 'LLL d, yyyy',
  week: "'Week of' LLL d, yyyy",
  month: 'LLL yyyy',
  quarter: "'Quarter from' LLL yyyy",
};

const TICK_DATE: Record<AnalyticsSeriesGrain, string> = {
  day: 'LLL d',
  week: 'LLL d',
  month: 'LLL yyyy',
  quarter: 'LLL yyyy',
};

const dateText = (start: string, format: string): string =>
  DateTime.fromISO(start, { zone: 'utc' }).setLocale('en-US').toFormat(format);

const fractionOf = (decimal: string): AnalyticsFraction => {
  const fraction = analyticsDecimalFraction(decimal);
  if (fraction === undefined) throw new Error(`"${decimal}" is not a decimal.`);
  return fraction;
};

const countText = (decimal: string): string =>
  GROUPED.format(BigInt(analyticsFractionDecimal(fractionOf(decimal), 0)));

const ceilDivide = (numerator: bigint, denominator: bigint): bigint =>
  numerator > 0n
    ? (numerator + denominator - 1n) / denominator
    : numerator / denominator;

const mostWhole = (
  fractions: readonly AnalyticsFraction[],
  sign: bigint,
): bigint =>
  fractions.reduce((most, { numerator, denominator }) => {
    const whole = ceilDivide(sign * numerator, denominator);
    return whole > most ? whole : most;
  }, 0n);

const niceStep = (least: bigint, magnitude = 1n): bigint =>
  NICE_MANTISSAS.map((mantissa) => mantissa * magnitude).find(
    (step) => step >= least,
  ) ?? niceStep(least, magnitude * DECADE);

const yAxisTicks = (
  lines: readonly AnalyticsSeriesLine[],
  metric: MetricsSeriesMetric,
): { tickvals: number[]; ticktext: string[]; range: [number, number] } => {
  const fractions = lines.flatMap(({ buckets }) =>
    buckets.map(({ value }) => fractionOf(value)),
  );
  const above = mostWhole(fractions, 1n);
  const below = mostWhole(fractions, -1n);
  const least = ceilDivide(above + below, TICK_INTERVALS);
  const step = niceStep(least > 0n ? least : 1n);
  const low = -ceilDivide(below, step);
  const high = ceilDivide(above, step);
  const steps = Array.from(
    { length: Number((high > 0n ? high : 1n) - low) + 1 },
    (_, index) => (low + BigInt(index)) * step,
  );
  const tickText = (value: bigint): string =>
    metric.unit === 'money'
      ? analyticsCompactCurrencyText(String(value), { millionPlaces: 1 })
      : GROUPED.format(value);
  return {
    tickvals: steps.map(Number),
    ticktext: steps.map(tickText),
    range: [Number(steps[0]), Number(steps[steps.length - 1])],
  };
};

const xAxisTicks = (starts: readonly string[], grain: AnalyticsSeriesGrain) => {
  const every = Math.ceil(starts.length / MAX_DATE_TICKS);
  const ticks = starts.filter((_, index) => index % every === 0);
  return {
    tickvals: ticks,
    ticktext: ticks.map((start) => dateText(start, TICK_DATE[grain])),
  };
};

const lineColor = (rank: number, token: MetricsSeriesToken): string =>
  rank === TOP_RANK
    ? token.colorPrimary
    : rank === ALL_OTHERS_RANK
      ? token.colorTextTertiary
      : token[RANK_COLORS[(rank - TOP_RANK - 1) % RANK_COLORS.length]];

const lineTrace = (
  line: AnalyticsSeriesLine,
  grain: AnalyticsSeriesGrain,
  metric: MetricsSeriesMetric,
  token: MetricsSeriesToken,
) => {
  const color = lineColor(line.rank, token);
  const valueText = (value: string): string =>
    metric.unit === 'money'
      ? analyticsCompactCurrencyText(value)
      : countText(value);
  return {
    type: 'scatter' as const,
    mode: 'lines+markers' as const,
    uid: `rank-${line.rank}`,
    legendrank: line.rank,
    name: line.name,
    x: line.buckets.map(({ start }) => start),
    y: line.buckets.map(({ value }) => Number(value)),
    text: line.buckets.map(
      ({ start, value }) =>
        `${dateText(start, HOVER_DATE[grain])} · ${valueText(value)}`,
    ),
    hovertemplate: '%{text}<extra>%{fullData.name}</extra>',
    line: { color, width: LINE_WIDTH },
    marker: { color, size: MARKER_SIZE },
  };
};

export const metricsSeriesFigure = (
  series: {
    grain: AnalyticsSeriesGrain;
    lines: readonly AnalyticsSeriesLine[];
  },
  metric: MetricsSeriesMetric,
  token: MetricsSeriesToken,
): MetricsSeriesFigure => {
  const lines = [...series.lines].sort((a, b) => a.rank - b.rank);
  const starts = lines[0]?.buckets.map(({ start }) => start) ?? [];
  const axis = {
    gridcolor: token.colorBorderSecondary,
    tickmode: 'array' as const,
  };
  return {
    data: lines.map((line) => lineTrace(line, series.grain, metric, token)),
    layout: {
      font: {
        family: token.fontFamily,
        size: token.fontSizeSM,
        color: token.colorTextSecondary,
      },
      paper_bgcolor: token.colorBgContainer,
      plot_bgcolor: token.colorBgContainer,
      showlegend: lines.length > 1,
      hovermode: 'closest',
      xaxis: {
        ...axis,
        ...xAxisTicks(starts, series.grain),
        title: { text: 'Date' },
        type: 'date',
        rangeslider: { visible: true },
      },
      yaxis: {
        ...axis,
        ...yAxisTicks(lines, metric),
        title: { text: metric.label },
      },
    },
    config: { displayModeBar: true, displaylogo: false, responsive: true },
  };
};
